import { useCallback, useEffect, useRef, useState } from 'react';
import type { LocalAudioTrack } from 'livekit-client';

/**
 * Manages @livekit/krisp-noise-filter on a LocalAudioTrack.
 *
 * The Krisp processor is lazy-loaded so it doesn't bloat the initial
 * bundle (it ships its own WASM ~700KB). Only loaded when the doctor
 * actually toggles noise cancellation ON.
 *
 * Usage:
 *   const audioTrack = localParticipant?.getTrackPublication(Track.Source.Microphone)?.audioTrack;
 *   const { enabled, supported, toggle, loading, error } = useNoiseCancellation(audioTrack);
 *
 * The processor is automatically removed when the track is unpublished
 * or when the component unmounts.
 */
export function useNoiseCancellation(audioTrack?: LocalAudioTrack | null) {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState<boolean | null>(null);

  // Cache the processor instance so toggles don't re-import
  const processorRef = useRef<unknown>(null);

  // Capability check on mount (browser support for AudioWorklet)
  useEffect(() => {
    const isSupported =
      typeof window !== 'undefined' &&
      typeof AudioContext !== 'undefined' &&
      'audioWorklet' in AudioContext.prototype;
    setSupported(isSupported);
  }, []);

  const enable = useCallback(async () => {
    if (!audioTrack) return;
    if (!supported) {
      setError('Su navegador no soporta cancelación de ruido.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (!processorRef.current) {
        const mod = await import(
          /* @vite-ignore */
          /* webpackChunkName: "livekit-krisp" */
          '@livekit/krisp-noise-filter'
        );
        // Type guard: KrispNoiseFilter export
        const KrispNoiseFilter =
          (mod as { KrispNoiseFilter?: () => unknown }).KrispNoiseFilter ??
          (mod as { default?: () => unknown }).default;
        if (typeof KrispNoiseFilter !== 'function') {
          throw new Error('No se pudo cargar el filtro de ruido (export inválido).');
        }
        processorRef.current = KrispNoiseFilter();
      }
      const processor = processorRef.current as Parameters<
        LocalAudioTrack['setProcessor']
      >[0];
      await audioTrack.setProcessor(processor);
      setEnabled(true);
    } catch (err) {
      console.error('[useNoiseCancellation] enable failed:', err);
      setError(err instanceof Error ? err.message : 'No se pudo activar la cancelación de ruido.');
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [audioTrack, supported]);

  const disable = useCallback(async () => {
    if (!audioTrack) return;
    setLoading(true);
    try {
      await audioTrack.stopProcessor();
      setEnabled(false);
    } catch (err) {
      console.error('[useNoiseCancellation] disable failed:', err);
    } finally {
      setLoading(false);
    }
  }, [audioTrack]);

  const toggle = useCallback(
    () => (enabled ? disable() : enable()),
    [enabled, enable, disable],
  );

  // Cleanup when the underlying track changes/unmounts
  useEffect(() => {
    return () => {
      if (audioTrack && enabled) {
        audioTrack.stopProcessor().catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioTrack]);

  return { enabled, loading, error, supported, toggle, enable, disable };
}
